import {defineStore} from "pinia";
import {profileTypes} from "@/common/types/profileTypes";
import SearchOptions, {SearchOptionsRequest} from '@/searchSection/types/SearchOptions'
import api from "@/searchSection/api";
import ProfileCard from "@/common/types/ProfileCardInterface";
import {ECitiesId} from "@/searchSection/types/ECitiesId";

export type RootState = {
    defaultOptions: SearchOptions,
    searchOptions: SearchOptions,
};

function getCount(count: number, options): number {
    Object.keys(options).forEach((key) => {
        if (options[key] && (options[key].from || options[key].to)) {
            if (options[key].from !== '') count++;
            if (options[key].to !== '') count++;
        }
    })

    return count
}

export const useSearchStore = defineStore({
    id: "searchStore",
    state: () => {
        return {
            defaultOptions: ({} as SearchOptions),
            searchOptions: {
                profileTypes: [1, 2, 3],
                age: {
                    from: '',
                    to: '',
                },
                height: {
                    from: '',
                    to: '',
                },
                bustSize: {
                    from: '',
                    to: '',
                },
                weight: {
                    from: '',
                    to: '',
                },
                priceInfo: {
                    typeChecked: false,
                    isNight: false,
                    type: 'home',
                    oneHour: {
                        from: '',
                        to: '',
                    },
                    twoHour: {
                        from: '',
                        to: '',
                    },
                },
                profileInfo: {
                    time: "allTime",
                    searchText: '',
                    withReviews: false,
                    verified: false,
                    withVideo: false,
                    withSocial: 'pass',
                },
                appearanceInfo: {
                    hairColor: null,
                    intimHaircut: null,
                },
                metroList: [],
                districtList: [],
                searchText: '',
                createdDate: '',
                withSocial: null,
                intimHairCut: null,
                hairColor: null,
                verified: false,
                withReviews: false,
                withVideo: false,
                additionalOptions: [],
                withTattoo: false,
                withPiercing: false,
                withSiliconeBreast: false,
                cityId: null,
            },
        } as RootState
    },

    getters: {
        searchOptionsRequest(): SearchOptionsRequest {
            return {
                profileTypes: this.searchOptions.profileTypes,
                age: {
                    from: this.searchOptions.age.from || 0,
                    to: this.searchOptions.age.to || 0,
                },
                height: {
                    from: this.searchOptions.height.from || 0,
                    to: this.searchOptions.height.to || 0,
                },
                bustSize: {
                    from: this.searchOptions.bustSize.from || 0,
                    to: this.searchOptions.bustSize.to || 0,
                },
                weight: {
                    from: this.searchOptions.weight.from || 0,
                    to: this.searchOptions.weight.to || 0,
                },
                priceInfo: {
                    type: this.searchOptions.priceInfo.typeChecked ? 'visit' : 'home',
                    typeChecked: this.searchOptions.priceInfo.typeChecked,
                    isNight: this.searchOptions.priceInfo.isNight,
                    oneHour: {
                        from: this.searchOptions.priceInfo.oneHour.from || 0,
                        to: this.searchOptions.priceInfo.oneHour.to || 0,
                    },
                    twoHour: {
                        from: this.searchOptions.priceInfo.twoHour.from || 0,
                        to: this.searchOptions.priceInfo.twoHour.to || 0,
                    },
                },
                createdDate: this.searchOptions.profileInfo.time,
                withSocial: this.searchOptions.profileInfo.withSocial !== 'pass',
                intimHairCut: this.searchOptions.appearanceInfo.intimHaircut || null,
                hairColor: this.searchOptions.appearanceInfo.hairColor || null,
                verified: this.searchOptions.profileInfo.verified,
                withReviews: this.searchOptions.profileInfo.withReviews,
                withVideo: this.searchOptions.profileInfo.withVideo,
                searchText: this.searchOptions.profileInfo.searchText,
                metroList: this.searchOptions.metroList,
                districtList: this.searchOptions.districtList,
                additionalOptions: this.searchOptions.additionalOptions,
                withTattoo: this.searchOptions.withTattoo,
                withPiercing: this.searchOptions.withPiercing,
                withSiliconeBreast: this.searchOptions.withSiliconeBreast,
                cityId: this.searchOptions.cityId,
            }
        },
        filtersSection(): number {
            let count = 0

            count += this.searchOptions.profileTypes.length

            count = getCount(count, this.searchOptions)
            count = getCount(count, this.searchOptions.priceInfo)

            this.searchOptions.priceInfo.typeChecked && count++
            this.searchOptions.priceInfo.isNight && count++
            
            return count
        },
        filtersProfiles() {
            let count = 0

            this.searchOptions.profileInfo.searchText && count++

            this.searchOptions.profileInfo.time !== 'allTime' && count++
            this.searchOptions.profileInfo.withSocial !== 'pass' && count++

            this.searchOptions.profileInfo.verified && count++
            this.searchOptions.profileInfo.withReviews && count++
            this.searchOptions.profileInfo.withVideo && count++

            return count
        },
        appearanceFilters() {
            let count = 0

            this.searchOptions.appearanceInfo.hairColor && count++
            this.searchOptions.appearanceInfo.intimHaircut && count++

            return count
        },
        locationFilters() {
            let count = 0

            count += this.searchOptions.metroList.length
            count += this.searchOptions.districtList.length

            return count
        },
        additionalOptionsFilters() {
            let count = 0

            count += this.searchOptions.additionalOptions.length

            return count
        }
    },

    actions: {
        setCityFromUrl(): void {
            const pathParts = window.location.pathname.split('/')
            const cityFromUrl = pathParts[1]
            this.searchOptions.cityId = cityFromUrl ? ECitiesId[cityFromUrl.toUpperCase() as keyof typeof ECitiesId] : null
        },
        setType(type: profileTypes): void {
            if (this.searchOptions.profileTypes.includes(type)) {
                this.searchOptions.profileTypes = this.searchOptions.profileTypes.filter((chosenType) => chosenType !== type)
            } else {
                this.searchOptions.profileTypes.push(type)
            }
        },
        setDefaultOptions(): void {
            this.defaultOptions = JSON.parse(JSON.stringify(this.searchOptions));
        },
        resetOptions(): void {
            this.searchOptions = JSON.parse(JSON.stringify(this.defaultOptions))
        },
        async searchProfiles(): Promise<ProfileCard[]> {
            this.setCityFromUrl()
            return await api.searchProfiles(this.searchOptionsRequest)
        },
    },
})